import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { faqApi } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../ui/accordion';
import { HelpCircle, Search, Loader2, ArrowLeft, Heart } from 'lucide-react';

/**
 * Öffentliche Hilfe/FAQ-Seite – auch ohne Login erreichbar.
 * Wird auf Login- und Registrierungsseite verlinkt, damit Interessenten
 * verstehen, warum sie sich registrieren sollen.
 *
 * Query-Parameter `?from=register` schaltet auf die kontextspezifische Variante:
 *  - Backend wird mit scope=public abgefragt (deckt 'both' + 'public_only' ab)
 *  - Zurück-Link führt auf /teilnehmer/registrieren statt /seminare
 */
export default function PublicFaqPage() {
    const [data, setData] = useState(null);
    const [search, setSearch] = useState('');
    const [searchParams] = useSearchParams();
    const fromRegister = searchParams.get('from') === 'register';

    useEffect(() => {
        // Default scope=public liefert sowohl 'both' als auch 'public_only'-Einträge.
        faqApi.publicList()
            .then(r => setData(r.data))
            .catch(() => setData({ categories: [] }));
    }, []);

    const filtered = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data.categories;
        return data.categories
            .map(c => ({
                ...c,
                faqs: c.faqs.filter(f =>
                    f.question.toLowerCase().includes(q) ||
                    (f.answer_html || '').toLowerCase().includes(q)
                ),
            }))
            .filter(c => c.faqs.length > 0);
    }, [data, search]);

    const backTo = fromRegister ? '/teilnehmer/registrieren' : '/seminare';
    const backLabel = fromRegister ? 'Zurück zur Registrierung' : 'Zurück zu den Seminaren';

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Header */}
            <header className="bg-secondary">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                    <Link to={backTo} className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm" data-testid="public-faq-back">
                        <ArrowLeft className="h-4 w-4" />
                        {backLabel}
                    </Link>
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-primary rounded-lg">
                            <Heart className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm font-bold text-white">BGPR-Akademie</span>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-10">
                <div className="flex items-center gap-2 mb-2">
                    <HelpCircle className="h-7 w-7 text-primary" />
                    <h1 className="text-2xl font-heading font-bold">Hilfe & Häufige Fragen</h1>
                </div>
                <p className="text-sm text-gray-600 mb-6 max-w-2xl">
                    Hier finden Sie Antworten auf die wichtigsten Fragen rund um Buchungen, Lizenzen
                    und Ihr Konto bei der BGPR-Akademie. Wenn Ihre Frage hier nicht beantwortet wird,
                    schreiben Sie uns einfach eine E-Mail.
                </p>

                <div className="relative mb-6 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Frage durchsuchen…"
                        className="pl-9"
                        data-testid="public-faq-search"
                    />
                </div>

                {!data ? (
                    <div className="py-16 text-center text-gray-500">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </div>
                ) : filtered.length === 0 ? (
                    <Card>
                        <CardContent className="py-10 text-center text-sm text-gray-500" data-testid="public-faq-empty">
                            {search ? 'Keine passenden Fragen gefunden.' : 'Aktuell sind noch keine FAQ-Einträge veröffentlicht.'}
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-6">
                        {filtered.map((cat) => (
                            <Card key={cat.id} data-testid={`public-faq-cat-${cat.id}`}>
                                <CardContent className="pt-5">
                                    <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                        {cat.label}
                                        <span className="text-xs font-normal text-gray-400">({cat.faqs.length})</span>
                                    </h2>
                                    <Accordion type="single" collapsible className="w-full">
                                        {cat.faqs.map((f) => (
                                            <AccordionItem key={f.id} value={f.id}>
                                                <AccordionTrigger className="text-left text-sm font-medium" data-testid={`public-faq-q-${f.id}`}>
                                                    {f.question}
                                                </AccordionTrigger>
                                                <AccordionContent>
                                                    <div
                                                        className="prose prose-sm max-w-none text-gray-700 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1"
                                                        dangerouslySetInnerHTML={{ __html: f.answer_html || '' }}
                                                    />
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                <div className="mt-10 flex items-center justify-between text-sm text-gray-600 border-t pt-6">
                    <Link to="/teilnehmer/login" className="hover:text-primary">Zum Login</Link>
                    <Link to="/teilnehmer/registrieren" className="hover:text-primary font-semibold" data-testid="public-faq-to-register">
                        Jetzt Konto anlegen →
                    </Link>
                </div>
            </main>

            <footer className="bg-secondary py-5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-xs text-gray-500">
                    Berliner Gesellschaft für Prävention und Rehabilitation von Herz-Kreislauferkrankungen e.V.
                </div>
            </footer>
        </div>
    );
}
